import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { omitReservedManagedAuthPrivateData } from '../config/r2rManagedAuthPrivateData';
import {
  CART_ROWS_KEY,
  cartItemCount,
  cartRowCount,
  computeNextCartRows,
  createCartRow,
  getCartRows,
  mergeCarts,
  removeCartRow,
  removeCartRows as dropCartRowsByIds,
  setCartRowDeliveryMethod,
  setCartRowQty,
} from '../util/cart';
import { clearStoredCart, readStoredCart, writeStoredCart } from '../util/cartStorage';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';

import { setCurrentUser } from './user.duck';

/** Rejection type for a listing that can never be carted (e.g. a tank service). */
export const CART_INELIGIBLE_LISTING = 'CART_INELIGIBLE_LISTING';

const PERSISTED_TO_ACCOUNT = 'account';
const PERSISTED_TO_DEVICE = 'device';

// ================ Selectors ================ //

/**
 * The cart lives in privateData once signed in and in localStorage before that.
 * Every thunk below works either way, so callers only ever read through here.
 *
 * @param {Object} state
 * @returns {Array<Object>}
 */
export const cartRowsSelector = state => {
  const currentUser = state.user?.currentUser;
  return currentUser ? getCartRows(currentUser) : state.cart?.deviceRows || [];
};

/**
 * @param {Object} state
 * @returns {number} unique listings, used for the row cap
 */
export const cartRowCountSelector = state => cartRowCount(cartRowsSelector(state));

/**
 * @param {Object} state
 * @returns {number} total units (sum of qty), used for the topbar badge
 */
export const cartItemCountSelector = state => cartItemCount(cartRowsSelector(state));

/**
 * @param {Object} state
 * @returns {{ carriedOver: number, conflicts: string[], droppedForCap: number }|null}
 */
export const cartMergeSummarySelector = state => state.cart?.mergeSummary || null;

// ================ Persistence ================ //

const withCartRows = (currentUser, rows) => ({
  ...currentUser,
  attributes: {
    ...currentUser.attributes,
    profile: {
      ...currentUser.attributes.profile,
      privateData: {
        ...currentUser.attributes.profile?.privateData,
        [CART_ROWS_KEY]: rows,
      },
    },
  },
});

/**
 * Persist rows wherever this buyer's cart lives.
 *
 * Signed in: optimistic update first so the badge and rows move immediately, then the
 * profile write, reverting the optimistic update if it fails. Signed out: localStorage.
 *
 * Resolves `{ ok: true, ... }` or `{ ok: false, error }` rather than throwing, so each
 * thunk decides what to do with a failed write.
 */
const persistCartRows = (rows, { getState, dispatch, extra: sdk }) => {
  const currentUser = getState().user?.currentUser;

  if (!currentUser) {
    writeStoredCart(rows);
    return Promise.resolve({ ok: true, rows, persistedTo: PERSISTED_TO_DEVICE });
  }

  dispatch(setCurrentUser(withCartRows(currentUser, rows)));

  // Reserved managed-auth / SSO keys must never ride along in a client patch.
  const privateData = omitReservedManagedAuthPrivateData({ [CART_ROWS_KEY]: rows });

  return sdk.currentUser
    .updateProfile({ privateData }, { expand: true })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(setCurrentUser(entities[0]));
      return { ok: true, rows, persistedTo: PERSISTED_TO_ACCOUNT };
    })
    .catch(e => {
      dispatch(setCurrentUser(currentUser));
      return { ok: false, error: storableError(e) };
    });
};

const notWriting = (_, { getState }) => !getState().cart?.updateInProgress;

// ================ Async Thunks ================ //

/**
 * Read the device cart on the client. Runs after mount, never during SSR, because the
 * server has no idea whether a cart exists.
 *
 * If a buyer is already signed in and still has device rows — they signed in on another
 * page, or the merge failed last time — the merge runs now.
 */
const hydrateCartThunk = createAsyncThunk('cart/hydrate', (_, thunkAPI) => {
  const { getState, dispatch } = thunkAPI;
  const deviceRows = readStoredCart();

  if (getState().user?.currentUser && deviceRows.length > 0) {
    dispatch(mergeDeviceCartIntoAccountThunk());
  }

  return { rows: deviceRows };
});

const addToCartThunk = createAsyncThunk(
  'cart/addToCart',
  ({ listing, qty, deliveryMethod }, thunkAPI) => {
    const { getState, rejectWithValue } = thunkAPI;

    const row = createCartRow(listing, { qty, deliveryMethod });
    if (!row) {
      return rejectWithValue({ type: CART_INELIGIBLE_LISTING });
    }

    const result = computeNextCartRows(cartRowsSelector(getState()), row);
    if (result.error) {
      return rejectWithValue({ type: result.error });
    }

    return persistCartRows(result.nextRows, thunkAPI).then(persisted =>
      persisted.ok ? persisted : rejectWithValue(persisted.error)
    );
  },
  { condition: notWriting }
);

const removeFromCartThunk = createAsyncThunk(
  'cart/removeFromCart',
  (listingId, thunkAPI) => {
    const { getState, rejectWithValue } = thunkAPI;
    const nextRows = removeCartRow(cartRowsSelector(getState()), listingId);

    return persistCartRows(nextRows, thunkAPI).then(persisted =>
      persisted.ok ? persisted : rejectWithValue(persisted.error)
    );
  },
  { condition: notWriting }
);

const removeCartRowsThunk = createAsyncThunk(
  'cart/removeCartRows',
  (listingIds, thunkAPI) => {
    const { getState, rejectWithValue } = thunkAPI;
    const nextRows = dropCartRowsByIds(cartRowsSelector(getState()), listingIds);

    return persistCartRows(nextRows, thunkAPI).then(persisted =>
      persisted.ok ? persisted : rejectWithValue(persisted.error)
    );
  },
  { condition: notWriting }
);

const updateCartRowQtyThunk = createAsyncThunk(
  'cart/updateCartRowQty',
  ({ listingId, qty }, thunkAPI) => {
    const { getState, rejectWithValue } = thunkAPI;
    const nextRows = setCartRowQty(cartRowsSelector(getState()), listingId, qty);

    return persistCartRows(nextRows, thunkAPI).then(persisted =>
      persisted.ok ? persisted : rejectWithValue(persisted.error)
    );
  },
  { condition: notWriting }
);

const updateCartRowDeliveryMethodThunk = createAsyncThunk(
  'cart/updateCartRowDeliveryMethod',
  ({ listingId, deliveryMethod }, thunkAPI) => {
    const { getState, rejectWithValue } = thunkAPI;
    const nextRows = setCartRowDeliveryMethod(
      cartRowsSelector(getState()),
      listingId,
      deliveryMethod
    );

    return persistCartRows(nextRows, thunkAPI).then(persisted =>
      persisted.ok ? persisted : rejectWithValue(persisted.error)
    );
  },
  { condition: notWriting }
);

/**
 * Fold the device cart into the account cart at login.
 *
 * The device cart is cleared only after a successful write, so a failed merge can be
 * retried on the next hydrate instead of losing the rows.
 */
const mergeDeviceCartIntoAccountThunk = createAsyncThunk(
  'cart/mergeDeviceCartIntoAccount',
  (_, thunkAPI) => {
    const { getState, rejectWithValue } = thunkAPI;
    const currentUser = getState().user?.currentUser;
    if (!currentUser) {
      return rejectWithValue({ type: 'CART_NOT_AUTHENTICATED' });
    }

    const deviceRows = readStoredCart();
    const accountRows = getCartRows(currentUser);
    const { rows, summary } = mergeCarts(deviceRows, accountRows);

    if (deviceRows.length === 0) {
      // Nothing to fold in, so nothing to write and nothing to report.
      clearStoredCart();
      return { rows: accountRows, summary };
    }

    return persistCartRows(rows, thunkAPI).then(persisted => {
      if (!persisted.ok) {
        return rejectWithValue(persisted.error);
      }
      clearStoredCart();
      return { rows, summary };
    });
  },
  { condition: notWriting }
);

// ================ Slice ================ //

const initialState = {
  // Guest cart only. Signed-in rows live in currentUser.profile.privateData.
  deviceRows: [],
  hydrated: false,
  updateInProgress: false,
  updateError: null,
  updatingListingId: null,
  mergeInProgress: false,
  mergeSummary: null,
};

const writeThunks = [
  addToCartThunk,
  removeFromCartThunk,
  removeCartRowsThunk,
  updateCartRowQtyThunk,
  updateCartRowDeliveryMethodThunk,
];

const listingIdOfWrite = arg => {
  if (typeof arg === 'string') {
    return arg;
  }
  return arg?.listingId || arg?.listing?.id?.uuid || null;
};

const applyWriteResult = (state, action) => {
  state.updateInProgress = false;
  state.updatingListingId = null;
  if (action.payload?.persistedTo === PERSISTED_TO_DEVICE) {
    state.deviceRows = action.payload.rows;
  }
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearUpdateError: state => {
      state.updateError = null;
    },
    clearMergeSummary: state => {
      state.mergeSummary = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(hydrateCartThunk.fulfilled, (state, action) => {
        state.hydrated = true;
        state.deviceRows = action.payload.rows;
      })
      .addCase(mergeDeviceCartIntoAccountThunk.pending, state => {
        state.mergeInProgress = true;
        state.updateInProgress = true;
      })
      .addCase(mergeDeviceCartIntoAccountThunk.fulfilled, (state, action) => {
        state.mergeInProgress = false;
        state.updateInProgress = false;
        state.deviceRows = [];
        // Report only what actually changed; an unchanged cart shows no notice at all.
        const { carriedOver, conflicts, droppedForCap } = action.payload.summary;
        const changed = carriedOver > 0 || conflicts.length > 0 || droppedForCap > 0;
        state.mergeSummary = changed ? action.payload.summary : null;
      })
      .addCase(mergeDeviceCartIntoAccountThunk.rejected, (state, action) => {
        state.mergeInProgress = false;
        state.updateInProgress = false;
        state.updateError = action.payload || null;
      });

    writeThunks.forEach(thunk => {
      builder
        .addCase(thunk.pending, (state, action) => {
          state.updateInProgress = true;
          state.updateError = null;
          state.updatingListingId = listingIdOfWrite(action.meta.arg);
        })
        .addCase(thunk.fulfilled, applyWriteResult)
        .addCase(thunk.rejected, (state, action) => {
          state.updateInProgress = false;
          state.updatingListingId = null;
          state.updateError = action.payload || null;
        });
    });
  },
});

export const { clearMergeSummary, clearUpdateError } = cartSlice.actions;

export default cartSlice.reducer;

// ================ Thunk Wrappers ================ //

/**
 * Hydrate the device cart from localStorage. Client-side only.
 */
export const hydrateCart = () => dispatch => dispatch(hydrateCartThunk());

/**
 * Add a listing to the cart after quantity and delivery method have been chosen.
 * Stock is not held — reservation starts at checkout.
 *
 * @param {Object} params
 * @param {Object} params.listing
 * @param {number} params.qty
 * @param {string} params.deliveryMethod 'shipping' | 'pickup'
 */
export const addToCart = params => dispatch => dispatch(addToCartThunk(params));

/**
 * @param {string|{uuid: string}} listingId
 */
export const removeFromCart = listingId => dispatch => dispatch(removeFromCartThunk(listingId));

/**
 * Drop every listing in a paid box. Other boxes stay in the cart.
 *
 * @param {Array<string|{uuid: string}>} listingIds
 */
export const removeCartRows = listingIds => dispatch => dispatch(removeCartRowsThunk(listingIds));

/**
 * @param {Object} params
 * @param {string} params.listingId
 * @param {number} params.qty
 */
export const updateCartRowQty = params => dispatch => dispatch(updateCartRowQtyThunk(params));

/**
 * Move a row into that seller's other box.
 *
 * @param {Object} params
 * @param {string} params.listingId
 * @param {string} params.deliveryMethod
 */
export const updateCartRowDeliveryMethod = params => dispatch =>
  dispatch(updateCartRowDeliveryMethodThunk(params));

/**
 * Fold the device cart into the account cart. Safe to dispatch more than once — the
 * device cart is cleared on success.
 */
export const mergeDeviceCartIntoAccount = () => dispatch =>
  dispatch(mergeDeviceCartIntoAccountThunk());
